import { beeXLogger } from '../../../helpers/logger';
import {
  editOffer,
  generateSdpFragment,
  parseOffer,
  supportsNonAdvertisedCodec,
} from '../utils';


let globalCodecsCache = null
let globalCodecsPromise = null

export async function getGlobalCodecs() {
  beeXLogger.debug('Checking codecs...')
  if (globalCodecsCache !== null) {
    return globalCodecsCache
  }

  if (globalCodecsPromise !== null) {
    return globalCodecsPromise
  }

  beeXLogger.debug('Starting codecs check...')
  globalCodecsPromise = Promise.all(
    [
      ['pcma/8000/2'],
      [
        'multiopus/48000/6',
        'channel_mapping=0,4,1,2,3,5;num_streams=4;coupled_streams=2',
      ],
      ['L16/48000/2'],
    ].map((c) =>
      supportsNonAdvertisedCodec(c[0], c[1]).then((r) => (r ? c[0] : false))
    )
  ).then(codecs => {
    const filtered = codecs.filter((e) => e !== false)
    globalCodecsCache = filtered
    globalCodecsPromise = null
    beeXLogger.debug('Supported codecs:', filtered)
    return filtered
  }).catch(err => {
    beeXLogger.error('Error checking codecs:', err)
    globalCodecsPromise = null
    return []
  })

  return globalCodecsPromise
}


export class RTCCodecAdapter {
  peerConnection = null;
  sessionUrl = '';
  offerData = null;
  queuedCandidates = [];
  restartTimeout = null;
  nonAdvertisedCodecs = [];
  RETRY_PAUSE = 2000;
  TIMEOUT_RENEW_STREAM = 6000;
  renewStreamTimeout = null;
  restartCount = 0;
  destroyed = false;



  async initializeCodecs() {
    return await getGlobalCodecs()
  }

  handleError = (
    err,
    stream,
    config,
    onSuccess,
    onError
  ) => {
    if (this.destroyed) {
      return;
    }

    console.log('Error: ========>', err);

    if (!this.restartTimeout && this.restartCount < 3) {
      this.restartCount++;

      if (this.peerConnection !== null) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      if (this.sessionUrl) {
        fetch(this.sessionUrl, {
          method: 'DELETE',
        }).catch(console.error);
      }

      this.sessionUrl = '';
      this.queuedCandidates = [];

      this.restartTimeout = window.setTimeout(() => {
        this.restartTimeout = null;
        this.connect(stream, config, onSuccess, onError);
      }, this.RETRY_PAUSE);
    } else {
      this.destroy();

      if (onError) {
        onError();
      }
    }
  };

  sendLocalCandidates = async (
    candidates,
    config
  ) => {
    if (this.sessionUrl && this.offerData && config.streamToken) {
      try {
        const res = await fetch(this.sessionUrl, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/trickle-ice-sdpfrag',
            'If-Match': '*',
            Authorization: `BeeX a:${config.streamToken}`,
          },
          body: generateSdpFragment(this.offerData, candidates),
        });

        switch (res.status) {
          case 204:
            break;
          case 404:
            throw new Error('stream not found');
          default:
            throw new Error(`bad status code ${res.status}`);
        }
      } catch (err) {
        console.log('Error ===========> ', err);
        throw err;
      }
    }
  };

  startTimeoutRenewStream = (callback) => {
    if (this.renewStreamTimeout) {
      clearTimeout(this.renewStreamTimeout);
    }

    this.renewStreamTimeout = window.setTimeout(() => {
      if (this.destroyed) {
        return;
      }

      this.renewStreamTimeout = null;
      if (callback) {
        callback();
      }
    }, this.TIMEOUT_RENEW_STREAM);
  };

  clearTimeoutRenewStream = () => {
    if (this.renewStreamTimeout) {
      clearTimeout(this.renewStreamTimeout);
      this.renewStreamTimeout = null;
    }
  };

  async connect(
    stream,
    config,
    onSuccess,
    onError
  ) {
    if (this.peerConnection || this.restartTimeout) {
      return;
    }

    // this.startTimeoutRenewStream(onError);
    console.log('URL', `rtsp://${stream.wsHost}:${10554}${stream.path}`);

    try {
      console.log('CONNECTING', JSON.parse(JSON.stringify(stream)));
      // Initialize codecs support
      this.nonAdvertisedCodecs = await this.initializeCodecs();

      // Create peer connection
      this.peerConnection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:turn01.beex.vn:3478' },
        ],
      });

      // Add transceivers
      const direction = 'sendrecv';
      this.peerConnection.addTransceiver('video', { direction });
      this.peerConnection.addTransceiver('audio', { direction });

      // Handle ICE candidates
      this.peerConnection.onicecandidate = (evt) => {
        if (this.restartTimeout) return;

        if (evt.candidate !== null) {
          if (this.sessionUrl === '') {
            this.queuedCandidates.push(evt.candidate);
          } else {
            this.sendLocalCandidates([evt.candidate], config).catch((err) =>
              this.handleError(
                err.toString(),
                stream,
                config,
                onSuccess,
                onError
              )
            );
          }
        }
      };

      // Handle connection state changes
      this.peerConnection.oniceconnectionstatechange = () => {
        if (this.restartTimeout !== null) return;

        if (this.peerConnection?.iceConnectionState === 'disconnected') {
          this.handleError(
            'peer connection closed',
            stream,
            config,
            onSuccess,
            onError
          );
        }
      };

      // Handle media tracks
      this.peerConnection.ontrack = (evt) => {
        if (this.destroyed) {
          return;
        }

        this.clearTimeoutRenewStream();
        onSuccess(evt.streams[0]);
      };

      // Create and send offer
      const offer = await this.peerConnection.createOffer();
      offer.sdp = editOffer(offer.sdp, this.nonAdvertisedCodecs);
      this.offerData = parseOffer(offer.sdp);

      await this.peerConnection.setLocalDescription(offer);

      const baseEndpoint = `https://${stream.wsHost}:${stream.wssPort}`;
      console.log('BASE ENDPOINT', baseEndpoint);
      const response = await fetch(`${baseEndpoint}${stream.path}/whep`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/sdp',
          Authorization: 'BeeX a:' + config.streamToken,
        },
        body: offer.sdp,
      });

      switch (response.status) {
        case 201:
          break;
        case 404:
          throw new Error('stream not found');
        case 400: {
          const error = await response.json();
          throw new Error(error.error);
        }
        default:
          throw new Error(`bad status code ${response.status}`);
      }

      this.sessionUrl = `${baseEndpoint}${response.headers.get('location')}`;
      const answerSdp = await response.text();

      if (!this.peerConnection || this.destroyed) {
        return;
      }

      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription({
          type: 'answer',
          sdp: answerSdp,
        })
      );

      if (this.queuedCandidates.length !== 0 && !this.destroyed) {
        await this.sendLocalCandidates(this.queuedCandidates, config);
        this.queuedCandidates = [];
      }
    } catch (err) {
      this.handleError(
        err.toString(),
        stream,
        config,
        onSuccess,
        onError
      );
    }
  }

  destroy() {
    this.destroyed = true;
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.sessionUrl) {
      fetch(this.sessionUrl, {
        method: 'DELETE',
      }).catch(console.error);
    }

    if (this.restartTimeout) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }

    if (this.renewStreamTimeout) {
      clearTimeout(this.renewStreamTimeout);
      this.renewStreamTimeout = null;
    }

    this.sessionUrl = '';
    this.offerData = null;
    this.queuedCandidates = [];
  }
}

export function preloadCodecs(page = "") {
  beeXLogger.debug('Preloading codecs...', page)
  return getGlobalCodecs()
}
